import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Alert, Breadcrumb, Button, Nav, Spinner, Table } from 'react-bootstrap';
import axiosInstance from '../../axios/axiosConfig';
import PdfViewerModal from '../common/PdfViewerModal';

const ROOT_FOLDER = 'Panel Shop Standard Work Procedures';
const SECTIONS = ['Standard Work Procedures', 'Testing Procedures'];
const normalizeName = (name) => String(name || '').trim().toLowerCase();
const isPublishedItem = (item) => item.folder
  ? !['original', 'archive', 'archives'].includes(normalizeName(item.name))
  : Boolean(item.file) && /\.pdf$/i.test(item.name);
const formatSize = (bytes) => {
  if (bytes == null) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function PanelShopWorkProcedures() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  // Use the same QMS document library as Engineering Design Documents.
  const siteId = process.env.REACT_APP_SHAREPOINT_BMS_SITE_ID;
  const driveId = process.env.REACT_APP_SHAREPOINT_BMS_DRIVE_ID;
  const graphBase = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}`;
  const [section, setSection] = useState(SECTIONS[0]);
  const [sectionFolders, setSectionFolders] = useState({});
  const [trail, setTrail] = useState([]);
  const [items, setItems] = useState([]);
  const [structureLoading, setStructureLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [structureError, setStructureError] = useState('');
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [preview, setPreview] = useState(null);
  const previewRequest = useRef(0);
  const previewUrl = useRef('');
  const folderId = trail.length ? trail[trail.length - 1].id : sectionFolders[section];

  const graphGet = useCallback(async (url, options = {}) => {
    if (!account) throw new Error('Please sign in to view work procedures.');
    const token = await instance.acquireTokenSilent({
      scopes: ['https://graph.microsoft.com/.default'],
      account,
    });
    return axiosInstance.get(url, {
      ...options,
      headers: { Authorization: `Bearer ${token.accessToken}` },
    });
  }, [instance, account]);

  const getChildren = useCallback(async (id, signal) => {
    let url = `${graphBase}/items/${encodeURIComponent(id)}/children?$top=200`;
    const children = [];
    while (url) {
      const response = await graphGet(url, { signal });
      children.push(...(response.data.value || []));
      url = response.data['@odata.nextLink'];
    }
    return children;
  }, [graphBase, graphGet]);

  useEffect(() => {
    const controller = new AbortController();
    let current = true;
    setStructureLoading(true);
    setStructureError('');
    setSectionFolders({});
    async function discover() {
      try {
        if (!siteId || !driveId) throw new Error('The QMS document library is not configured. Please contact an administrator.');
        const rootItems = await getChildren('root', controller.signal);
        const root = rootItems.find((item) => item.folder && normalizeName(item.name) === normalizeName(ROOT_FOLDER));
        if (!root) throw new Error(`Could not find "${ROOT_FOLDER}" in the QMS document library.`);
        const children = await getChildren(root.id, controller.signal);
        const folders = {};
        SECTIONS.forEach((name) => {
          const match = children.find((item) => item.folder && normalizeName(item.name) === normalizeName(name));
          if (match) folders[name] = match.id;
        });
        if (current) setSectionFolders(folders);
      } catch (err) {
        if (current) setStructureError(err.message || 'Unable to load work procedures. Please try again.');
      } finally {
        if (current) setStructureLoading(false);
      }
    }
    discover();
    return () => { current = false; controller.abort(); };
  }, [siteId, driveId, getChildren, refresh]);

  useEffect(() => {
    const controller = new AbortController();
    let current = true;
    setItems([]);
    setError('');
    setLoading(Boolean(folderId));
    if (folderId) {
      getChildren(folderId, controller.signal)
        .then((children) => {
          if (current) setItems(children.filter(isPublishedItem).sort((a, b) =>
            Number(Boolean(b.folder)) - Number(Boolean(a.folder)) || a.name.localeCompare(b.name)));
        })
        .catch(() => { if (current) setError('Unable to load this folder. Please refresh and try again.'); })
        .finally(() => { if (current) setLoading(false); });
    }
    return () => { current = false; controller.abort(); };
  }, [folderId, getChildren, refresh]);

  const closePreview = useCallback(() => {
    previewRequest.current += 1;
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
    previewUrl.current = '';
    setPreview(null);
  }, []);

  useEffect(() => () => {
    previewRequest.current += 1;
    if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
  }, []);

  const viewPdf = async (item) => {
    closePreview();
    const requestId = previewRequest.current;
    setPreview({ title: item.name, externalUrl: item.webUrl || '', loading: true, viewerUrl: '' });
    try {
      const response = await graphGet(`${graphBase}/items/${encodeURIComponent(item.id)}/content`, { responseType: 'blob' });
      if (requestId !== previewRequest.current) return;
      previewUrl.current = URL.createObjectURL(response.data);
      setPreview((previous) => ({ ...previous, loading: false, viewerUrl: previewUrl.current }));
    } catch (_) {
      if (requestId === previewRequest.current) setPreview((previous) => ({ ...previous, loading: false }));
    }
  };

  const busy = structureLoading || loading;
  return (
    <section className="p-3" aria-label="Work Procedures">
      <div className="d-flex justify-content-end mb-3">
        <Button variant="outline-primary" disabled={busy} onClick={() => setRefresh((value) => value + 1)}>Refresh</Button>
      </div>
      <Nav variant="tabs" activeKey={section} className="mb-3" onSelect={(name) => {
        if (name) { setSection(name); setTrail([]); }
      }}>
        {SECTIONS.map((name) => (
          <Nav.Item key={name}><Nav.Link eventKey={name}>{name}</Nav.Link></Nav.Item>
        ))}
      </Nav>
      <Breadcrumb aria-label="Procedure folders">
        <Breadcrumb.Item active={!trail.length} onClick={() => setTrail([])}>{section}</Breadcrumb.Item>
        {trail.map((folder, index) => (
          <Breadcrumb.Item key={folder.id} active={index === trail.length - 1} onClick={() => setTrail((previous) => previous.slice(0, index + 1))}>
            {folder.name}
          </Breadcrumb.Item>
        ))}
      </Breadcrumb>
      {structureError && <Alert variant="danger">{structureError}</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}
      {busy && <div className="text-center p-3"><Spinner animation="border" role="status"><span className="visually-hidden">Loading work procedures...</span></Spinner></div>}
      {!busy && !structureError && !error && !folderId && <Alert variant="warning">The "{section}" folder was not found in {ROOT_FOLDER}.</Alert>}
      {!busy && !structureError && !error && folderId && (items.length ? (
        <Table striped bordered hover responsive>
          <thead className="table-light"><tr><th>Name</th><th>Modified</th><th>Size</th><th>Actions</th></tr></thead>
          <tbody>{items.map((item) => (
            <tr key={item.id}>
              <td>{item.folder ? (
                <Button variant="link" className="p-0 text-start" onClick={() => setTrail((previous) => [...previous, { id: item.id, name: item.name }])}>
                  <i className="bi bi-folder me-2" aria-hidden="true" />{item.name}
                </Button>
              ) : item.name}</td>
              <td>{item.lastModifiedDateTime ? new Date(item.lastModifiedDateTime).toLocaleDateString() : '-'}</td>
              <td>{item.folder ? '-' : formatSize(item.size)}</td>
              <td>{!item.folder && <div className="d-flex flex-wrap gap-2">
                <Button size="sm" onClick={() => viewPdf(item)}>View</Button>
                {item['@microsoft.graph.downloadUrl'] && <Button as="a" variant="outline-secondary" size="sm" href={item['@microsoft.graph.downloadUrl']} target="_blank" rel="noopener noreferrer">Download</Button>}
              </div>}</td>
            </tr>
          ))}</tbody>
        </Table>
      ) : <Alert variant="info">No published PDFs or procedure folders were found here.</Alert>)}
      <PdfViewerModal show={Boolean(preview)} onHide={closePreview} title={preview?.title || ''} loading={Boolean(preview?.loading)} viewerUrl={preview?.viewerUrl || ''} externalUrl={preview?.externalUrl || ''} />
    </section>
  );
}
